// react
import { ReactNode } from 'react';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';

// material ui
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

interface FaqQuestionProps {
  id: string;
  title: string;
  icon?: ReactNode;
  details: ReactNode;
  tour?: boolean;
  tourKey?: string;
  tourPath?: string;
  tourLabel?: string;
  handleChange: (id: string) => void;
  expanded: boolean;
}

const FaqQuestion = ({
  id,
  title,
  icon,
  details,
  tour,
  tourKey,
  tourPath,
  tourLabel,
  handleChange,
  expanded,
}: FaqQuestionProps) => {
  const params = useParams({ strict: false });
  const resolvedTourPath =
    tourPath && params?.accountId
      ? tourPath.replace('$accountId', params.accountId)
      : tourPath;
  const tourHref =
    tour && resolvedTourPath && tourKey
      ? `${resolvedTourPath}?tour=${encodeURIComponent(tourKey)}`
      : undefined;

  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      onChange={() => handleChange(id)}
      sx={{ background: 'transparent' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${id}-content`}
        id={`${id}-header`}
        sx={{ border: 'none !important' }}
      >
        <ListItem disablePadding>
          <ListItemIcon>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
              }}
            >
              {icon}
            </Box>
          </ListItemIcon>
          <ListItemText
            primary={title}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 600 }
            }}
          />
        </ListItem>
      </AccordionSummary>
      <AccordionDetails
        sx={{ border: 'none !important', color: 'text.secondary', pl: 4 }}
      >
        <Typography variant="body1">{details}</Typography>
        {tourHref && (
          <Button
            startIcon={<SchoolRoundedIcon />}
            endIcon={<ArrowForwardRoundedIcon />}
            variant="contained"
            component="a"
            href={tourHref}
            sx={{
              mt: 2,
              ':hover': {
                textDecoration: 'none',
                color: (theme) =>
                  theme.palette.mode === 'light' ? 'white' : 'black',
              },
            }}
          >
            {tourLabel || 'Start guided tour'}
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default FaqQuestion;
